import { useState, type FormEvent } from 'react'
import { checkField as checkFor, checkedKeys, fieldId, type CheckedKey } from './checkoutRules'
import type { Country } from '../../core/types/requests'

type Values = Record<CheckedKey, string>

/**
 * Directe feedback in de checkout: een veld wordt pas beoordeeld als je het verlaat
 * (niet tijdens het typen van je eerste letter), en een fout verdwijnt zodra je hem
 * herstelt. Bij versturen worden alle velden gecontroleerd en springt de focus naar
 * de eerste fout, vóórdat er iets naar de server gaat.
 */
export function useFieldChecks(values: Values, serverErrors: Record<string, string>, country: Country = 'NL') {
  const [local, setLocal] = useState<Partial<Record<CheckedKey, string>>>({})
  const checkField = (key: CheckedKey, value: string) => checkFor(key, value, country)

  const blur = (key: CheckedKey) => setLocal((l) => ({ ...l, [key]: checkField(key, values[key]) }))

  /** Tijdens typen alleen opnieuw beoordelen als er al een fout stond: nooit eerder "boos" worden. */
  const change = (key: CheckedKey, next: string) => {
    if (local[key]) setLocal((l) => ({ ...l, [key]: checkField(key, next) }))
  }

  const errorOf = (key: string): string | undefined => (local as Record<string, string | undefined>)[key] || serverErrors[key] || undefined

  /** Alles nalopen; true = mag door naar de server. */
  const checkAll = (): boolean => {
    const next = Object.fromEntries(checkedKeys.map((k) => [k, checkField(k, values[k])])) as Record<CheckedKey, string>
    setLocal(next)
    const first = checkedKeys.find((k) => next[k])
    if (first) document.getElementById(fieldId(first))?.focus()
    return !first
  }

  const guardSubmit = (onValid: (e: FormEvent) => void) => (e: FormEvent) => {
    if (!checkAll()) {
      e.preventDefault()
      return
    }
    onValid(e)
  }

  return { blur, change, errorOf, guardSubmit }
}
